import { useCallback, useEffect, useState } from 'react';
import { type Novel, SAMPLE_NOVELS } from '../data/samples';
import { getCustomNovels, requestPersistStorage, saveCustomNovels } from '../utils/indexedDB';
import { CustomNovelsArraySchema } from '../utils/validation/storageSchemas';

// モジュールグローバルで初期化 Promise とキャッシュを管理
let initPromise: Promise<Novel[]> | null = null;
let cachedNovels: Novel[] = [];
let isInitialized = false;

const ensureInitialized = (): Promise<Novel[]> => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    // 1. PWA永続化リクエスト (非同期で行う)
    requestPersistStorage().catch((err) =>
      console.error('Failed to request persist storage:', err),
    );

    try {
      // 2. LocalStorage からのマイグレーションチェック
      const savedLocalStorage = localStorage.getItem('novelflow_custom_novels');

      if (savedLocalStorage) {
        try {
          const parsedLocalStorage = JSON.parse(savedLocalStorage);
          const result = CustomNovelsArraySchema.safeParse(parsedLocalStorage);

          if (result.success && result.data.length > 0) {
            console.log('Migrating data from LocalStorage to IndexedDB...');
            // 競合を防ぐため先に LocalStorage から削除
            localStorage.removeItem('novelflow_custom_novels');
            // IndexedDB へ保存
            await saveCustomNovels(result.data);

            // すでにキャッシュにあるデータ（起動直後に手動インポートされたものなど）とマージ
            const merged = [...cachedNovels];
            const existingIds = new Set(merged.map((m) => m.id));
            for (const n of result.data) {
              if (!existingIds.has(n.id)) {
                merged.push(n as Novel);
                existingIds.add(n.id);
              }
            }
            cachedNovels = merged;
            isInitialized = true;
            return cachedNovels;
          }
          if (!result.success) {
            console.warn('LocalStorage data migration validation failed:', result.error);
          }
        } catch (e) {
          console.error('Failed to parse and migrate from LocalStorage:', e);
        }
        // 壊れたデータや空配列の場合も削除
        localStorage.removeItem('novelflow_custom_novels');
      }

      // 3. IndexedDB から最新データをロード
      const dbNovels = await getCustomNovels();

      // すでにメモリキャッシュにあるデータと IndexedDB のデータを重複なくマージ
      const merged = [...cachedNovels];
      const existingIds = new Set(merged.map((n) => n.id));
      for (const dbNovel of dbNovels) {
        if (!existingIds.has(dbNovel.id)) {
          merged.push(dbNovel);
          existingIds.add(dbNovel.id);
        }
      }
      cachedNovels = merged;
    } catch (error) {
      console.error('Failed to load custom novels:', error);
    }
    isInitialized = true;
    return cachedNovels;
  })();

  return initPromise;
};

export const useCustomNovels = () => {
  const [customNovels, setCustomNovels] = useState<Novel[]>(cachedNovels);
  const [novels, setNovels] = useState<Novel[]>(() => [...SAMPLE_NOVELS, ...cachedNovels]);
  const [isLoading, setIsLoading] = useState(!isInitialized);

  useEffect(() => {
    let active = true;

    if (!isInitialized) {
      ensureInitialized()
        .then(() => {
          if (active) {
            // マージ済みのキャッシュデータを使用する
            setCustomNovels(cachedNovels);
            setNovels([...SAMPLE_NOVELS, ...cachedNovels]);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.error('Failed to initialize custom novels:', err);
          if (active) {
            setIsLoading(false);
          }
        });
    } else {
      // 既に初期化済みの場合は、最新のキャッシュと同期する
      setCustomNovels(cachedNovels);
      setNovels([...SAMPLE_NOVELS, ...cachedNovels]);
      setIsLoading(false);
    }

    return () => {
      active = false;
    };
  }, []);

  const updateCustomNovels = useCallback(async (newCustom: Novel[]) => {
    // キャッシュを同期的に更新して別インスタンスからも参照可能にする
    cachedNovels = newCustom;

    // 状態を先行して更新（楽観的UI更新）
    setCustomNovels(newCustom);
    setNovels([...SAMPLE_NOVELS, ...newCustom]);

    try {
      await saveCustomNovels(newCustom);
    } catch (e) {
      console.error('Failed to save custom novels:', e);
      alert('小説データの保存に失敗しました。容量が不足している可能性があります。');
    }
  }, []);

  return {
    customNovels,
    novels,
    updateCustomNovels,
    isLoading,
  };
};
